import { useEffect, useMemo } from "react";
import { useFormikContext } from "formik";

import { useFormData } from "../../../contexts/FormData";

import { Grid, Divider, FormHelperText } from "@mui/material";

import { useQuery } from "@tanstack/react-query";

import { getInventoryPricing } from "../../InventoryPricing/apiService"

import Field from "../../../utils/FormikFields/Field";

import { startCase } from 'lodash';

const getScore = (rule) => {
  let score = 0;
  if (rule.colors?.length > 0) score++;
  if (rule.types?.length > 0) score++;
  if (rule.lines?.length > 0) score++;
  return score;
};


const Prices = () => {
  const { values, setFieldValue, initialValues } = useFormikContext() || {};

  const isInventoryEdit = values?.inventory_id; // new, check for inventory edit
  const isSold = values?.status === "sold" ? true : false;

  // needed for edit
  const inputsChanged = values.length !== initialValues.length ||
      values.material !== initialValues.material ||
      values.color !== initialValues.color ||
      values.line !== initialValues.line ||
      values.brand_name !== initialValues.brand_name;

      // console.log('inputsChanged', values.length !== initialValues.length, values.material !== initialValues.material, values.color !== initialValues.color, values.line !== initialValues.line, values.brand_name !== initialValues.brand_name);

  const { formValues, setFormValues } = useFormData() || {};

  const { data: inventory_pricing } = useQuery({
    queryKey: ['inventory_pricing', values?.brand_name],
    queryFn: () => getInventoryPricing(values?.brand_name),
    enabled: !!values?.brand_name,
    staleTime: Infinity
  });

  let disabled = !values?.brand_name

  const matchedPricing = useMemo(() => {
    const { length, color, material, brand_name, line } = values || {};

    if (!brand_name || !inventory_pricing?.length || !length) return null;

    // 1. Find the correct length bracket
    // Sort descending and find the first length <= requested length
    const sortedByLength = [...inventory_pricing].sort((a, b) => Number(b.length) - Number(a.length));
    const bracketEntry = sortedByLength.find(el => Number(el.length) <= Number(length));

    if (!bracketEntry) return null;
    const currentBracketLength = bracketEntry.length;

    // 2. Identify all valid candidates within this length bracket
    // A candidate is valid if the item's property matches OR if the rule is a catch-all (empty array)
    const candidates = inventory_pricing
      .filter(el => el.length === currentBracketLength)
      .filter(el => {
        const colorMatch = !el.colors?.length || el.colors.includes(color?.toUpperCase());
        const typeMatch = !el.types?.length || el.types.map(t => t?.toUpperCase()).includes(material?.toUpperCase());
        const lineMatch = !el.lines?.length || el.lines.map(l => l?.toUpperCase()).includes(line?.toUpperCase());

        return colorMatch && typeMatch && lineMatch;
      });

    if (candidates.length === 0) return null;

    // 3. Rank by Specificity
    // We calculate a "score" based on how many specific constraints are defined.
    // A rule with a 'line' defined is more specific than a 'catch-all'.
    const ranked = candidates.sort((a, b) => {
      return getScore(b) - getScore(a); // Highest score wins
    });

    // Return the most specific match
    return ranked[0] || null;

  }, [
    values?.length, 
    values?.color, 
    values?.material, 
    values?.line, 
    values?.brand_name,
    inventory_pricing
  ]);


  useEffect(() => {
    if (formValues?.inventory_item || formValues?.selectedWig) {
      return;
    }
    if (!inputsChanged && values.inventory_id) return; // edit mode + values still == DB baseline → keep DB price

    if (matchedPricing) {
      setFieldValue("cost_subtotal", matchedPricing.cost);
      setFieldValue("price", matchedPricing.price);
      // setFieldValue("service_price", matchedPricing.service_price);
    } else {
      // Only clear if we have enough info to expect a price
      if (values?.length) {
        setFieldValue("cost_subtotal", "");
        setFieldValue("price", "");
        setFieldValue("service_price", "");
      }
    }
  }, [matchedPricing, setFieldValue, formValues?.inventory_item, formValues?.selectedWig, inputsChanged, values.inventory_id]);

  return (
    <>
      <Grid size={{xs: 12, lg: 4}}>
        <Field name="cost_subtotal" label="Cost" disabled={disabled} />
        {isInventoryEdit && isSold ? <FormHelperText>This item is sold, updating the cost will update the cost on the bill</FormHelperText> : null}
      </Grid>

      <Grid size={{xs: 12, lg: 4}}>
        <Field name="price" label="Price" disabled={disabled} />
        {isInventoryEdit && isSold ? <FormHelperText>This item is sold, updating the price will update the price on the customer invoice</FormHelperText> : null}
      </Grid>

      <Grid size={{xs: 12, lg: 4}}>
        <Field name="service_price" label="Service Price" disabled={disabled} helperText="Optional - for tax purposes" />
      </Grid>

    </>
  )
};

export default Prices;